import React from 'react';
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  Navigate,
  useLocation,
} from 'react-router-dom';
import Login from './views/login/login';
import Register from './views/register/register';
import { useDispatch, useSelector } from 'react-redux';
import tokenService from './utils/tokenService';
import AuthProvider from './components/authProvider';
import MerchantAcceptanceGuard from './components/merchantAcceptanceGuard';
import Layout from './components/layout/layout';
import { setIsAuthenticated } from './store/slices/authSlice';
import Orders from './views/orders/orders';
import Customers from './views/customers/customers';
import CustomerOrders from './views/customers/orders/orders';
import Stores from './views/stores/stores';
import Report from './views/report/report';
import Permission from './views/settings/permission/permission';
import Company from './views/company/company';
import Menus from './views/menus/menus';
import MenuImport from './views/menu-import/menu-import';
import Inventory from './views/inventory/inventory';
import Coupons from './views/coupons/coupons';
import ArchivedCampaigns from './views/coupons/ArchivedCampaigns';
import DashboardContent from './views/dashboard/dashboard';
import StoreDetail from './views/stores/store-detail/store-detail';
import CompanyDetail from './views/company/company-detail/company-detail';
import Profile from './views/profile/profile';
import Users from './views/users/users';
import StoreSettings from './views/stores/store-settings/store-settings';
import OrderDetail from './views/orders/order-detail/order-detail';
import LiveOrders from './views/orders/live-orders/live-orders';
import OrderList from './views/company/company-detail/order-list';
import OrdersSummary from './views/orders/orders-summary/orders-summary';
import RefundApprovals from './views/orders/refund-approvals/refund-approvals';
import Subscriptions from './views/subscriptions/subscriptions';
import Templates from './views/templates/templates';
import FinanceTypes from './views/finance-types/finance-types';
import Preferences from './views/preferences/preferences';
import Subscription from './views/subscription/subscription';
import Receipt from './views/receipt/receipt';
import ForgotPassword from './views/forgot-password/forgot-password';
import NewPassword from './views/forgot-password/new-password/new-password';
import TurnstileBridge from './views/turnstile-bridge/turnstile-bridge';
import TermsAndConditions from './views/terms-and-conditions/terms-and-conditions';
import FinanceSection from './views/finance/section/finance-section';
import FinanceOverview from './views/finance/pages/overview';
import FinanceOrderReports from './views/finance/pages/order-reports';
import FinancePayouts from './views/finance/pages/payouts';
import FinancePayoutDetail from './views/finance/pages/payout-detail';
import FinanceLegacyTransactions from './views/finance/pages/legacy-transactions';
import FinanceDisputes from './views/finance/pages/disputes';
import FinanceTaxDocuments from './views/finance/pages/tax-documents';
import FinanceBusinessDetails from './views/finance/pages/business-details';
import FinanceBankAccount from './views/finance/pages/bank-account';
import CompanySales from './views/report/company-sales/company-sales';
import Kiosks from './views/kiosks/kiosks';
import ReportStore from './views/report-store/report-store';
import LiveTimeReport from './views/report/live-time/live-time';
import Reports2 from './views/report/reports-2/reports-2';
import ContactUs from './views/contact-us/contact-us';
import Integrations from './views/integrations/integrations';
import SquareCallback from './views/square/Callback';
import CloverCallback from './views/clover/Callback';
import Tags from './views/tags/tags';
import BlockedNumbers from './views/blocked-numbers/blocked-numbers';
import PrinterProfiles from './views/printer-profiles/printer-profiles';
import TermAcceptances from './views/terms-and-conditions/term-acceptances';
import Referral from './views/referral/referral';
import ReferralAdmin from './views/referral-admin/referral-admin';
import ImageLibraryAdmin from './views/image-library-admin/image-library-admin';
import Ratings from './views/ratings/ratings';
import LoginLogs from './views/login-logs/login-logs';
import Legal from './views/legal/legal';
import ReleaseNotes from './views/release-notes/release-notes';
import ReleaseNote from './views/release-notes/release-note';
import ReleaseNotesAdmin from './views/release-notes-admin/release-notes-admin';
import ReleaseNoteEditor from './views/release-notes-admin/release-note-editor';
import RouteError from './components/route-error/route-error';

// The two admin finance pages that became tabs (spec 2026-09-24). A
// bookmark keeps landing somewhere true — the house rule from
// /dashboard/finance/refunds — and keeps its own query string (Platform
// Finance carried its date range in the URL).
const ToTab = ({ tab }) => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('tab', tab);
  return <Navigate to={`/dashboard/finance?${params.toString()}`} replace />;
};

export const financeRedirects = [
  { path: '/dashboard/platform-finance', element: <ToTab tab="revenue" /> },
  { path: '/dashboard/finance/account-activity', element: <ToTab tab="ledger" /> },
];

// Spec 2026-10-01 manager app §5. Public on purpose: the manager mobile app
// loads this page in a WebView to get a Turnstile token before anyone has
// signed in, so it sits beside /forgot-password, outside the dashboard
// layout and its AuthProvider. Exported so its test can prove that.
export const turnstileBridgeRoute = {
  path: '/turnstile-bridge',
  element: <TurnstileBridge />,
};

const Router = () => {
  const dispatch = useDispatch();
  const roles = useSelector((state) => state.authReducer.roles);
  const routes = [
    {
      path: '',
      element: <DashboardContent />,
      index: true,
      roles: [100, 150, 200, 550],
    },
    {
      path: 'companies',
      element: <Outlet />,
      roles: [100, 150, 200],
      children: [
        {
          index: true,
          element: <Company />,
          roles: [100, 150, 200],
        },
        {
          path: '/dashboard/companies/:id',
          element: <CompanyDetail />,
          roles: [100, 150, 200],
        },
        {
          path: '/dashboard/companies/:id/orders',
          element: <OrderList />,
          roles: [100, 150, 200, 250],
        },
      ],
    },
    {
      path: 'orders',
      element: <Outlet />,
      roles: [100, 150, 200, 250, 550],
      children: [
        {
          index: true,
          element: <Orders />,
          roles: [100, 150, 200, 250, 550],
        },
        {
          path: '/dashboard/orders/:id',
          element: <OrderDetail />,
          roles: [100, 150, 200, 250, 550],
        },
        {
          path: '/dashboard/orders/summary',
          element: <OrdersSummary />,
          roles: [100],
        },
        {
          path: '/dashboard/orders/refund-approvals',
          element: <RefundApprovals />,
          roles: [100],
        },
        {
          path: '/dashboard/orders/:storeid/live',
          element: <LiveOrders />,
          roles: [100, 150, 200, 250, 550],
        },
      ],
    },
    {
      // Restoran tarafı (rol 150/200/250/550) geçici kapatıldı — backend
      // /Customer/Counts şirket kapsamı tutarsızlığı çözülünce geri açılacak.
      path: 'customers',
      element: <Outlet />,
      roles: [100],
      children: [
        {
          index: true,
          element: <Customers />,
          roles: [100],
        },
        {
          path: '/dashboard/customers/:id/orders',
          element: <CustomerOrders />,
          roles: [100],
        },
      ],
    },
    {
      path: 'kiosks',
      element: <Outlet />,
      roles: [100],
      children: [
        {
          index: true,
          element: <Kiosks />,
          roles: [100],
        },
      ],
    },
    {
      path: 'menus',
      element: <Outlet />,
      roles: [100, 150, 200, 250, 300],
      children: [
        {
          index: true,
          element: <Menus />,
          roles: [100, 150, 200, 250, 300],
        },
        {
          path: 'import',
          element: <MenuImport />,
          roles: [100, 150, 200, 250, 300],
        },
      ],
    },
    {
      path: 'inventory',
      element: <Outlet />,
      roles: [100, 150, 200, 250, 300],
      children: [
        {
          index: true,
          element: <Inventory />,
          roles: [100, 150, 200, 250, 300],
        },
      ],
    },
    {
      path: 'stores',
      element: <Outlet />,
      roles: [100, 150, 200],
      children: [
        {
          index: true,
          element: <Stores />,
          roles: [100, 150, 200],
        },
        {
          path: '/dashboard/stores/:id',
          element: <StoreDetail />,
          roles: [100, 150, 200],
        },
        {
          path: '/dashboard/stores/:id/settings',
          element: <StoreSettings />,
          roles: [100, 150, 200],
        },
      ],
    },
    {
      path: 'finance',
      element: <FinanceSection />,
      roles: [100, 150, 200, 550],
      children: [
        { index: true, element: <FinanceOverview />, roles: [100, 150, 200, 550] },
        { path: '/dashboard/finance/order-reports', element: <FinanceOrderReports />, roles: [150, 200, 550] },
        { path: '/dashboard/finance/payouts', element: <FinancePayouts />, roles: [150, 200] },
        // One payout, and the transactions that add up to it (spec
        // 2026-09-22 §6). Open to the accountant as well as the two managing
        // roles: there is nothing to manage on it. Connect-only comes free —
        // finance-section.js gates on the first path segment, which is
        // already 'payouts'.
        { path: '/dashboard/finance/payouts/:payoutId', element: <FinancePayoutDetail />, roles: [150, 200, 550] },
        // Reached from the Payouts header, not from the sidebar: it is a
        // company's own archive, worth a page but not a standing entry.
        { path: '/dashboard/finance/legacy', element: <FinanceLegacyTransactions />, roles: [150, 200] },
        {
          // Refunds is neither a page nor a tab any more (2026-09-21): a
          // refunded order is a row of the statement whose Refund column is
          // non-zero. The route stays so a bookmark still lands somewhere
          // true rather than on the dashboard with no explanation.
          path: '/dashboard/finance/refunds',
          element: <Navigate to="/dashboard/finance/order-reports" replace />,
          roles: [150, 200, 550],
        },
        { path: '/dashboard/finance/disputes', element: <FinanceDisputes />, roles: [150, 200] },
        { path: '/dashboard/finance/tax-documents', element: <FinanceTaxDocuments />, roles: [150, 200] },
        { path: '/dashboard/finance/business-details', element: <FinanceBusinessDetails />, roles: [150, 200] },
        // The page went by Account for two days (2026-09-15 to 09-17) and the
        // URL went with it. Both spellings resolve; this one is the shorter
        // lived of the two, so it is the one that redirects.
        {
          path: '/dashboard/finance/account',
          element: <Navigate to="/dashboard/finance/business-details" replace />,
          roles: [150, 200],
        },
        { path: '/dashboard/finance/bank-account', element: <FinanceBankAccount />, roles: [150, 200] },
        { path: '/dashboard/finance/:id/orders', element: <OrderList />, roles: [100, 150, 200, 550] },
        { path: '/dashboard/finance/account-activity', element: <ToTab tab="ledger" />, roles: [100] },
      ],
    },
    {
      path: 'platform-finance',
      element: <ToTab tab="revenue" />,
      roles: [100],
    },
    {
      path: 'reports',
      element: <Outlet />,
      roles: [100],
      children: [
        {
          index: true,
          element: <Report />,
          roles: [100],
        },
        {
          path: '/dashboard/reports/company-sales',
          element: <CompanySales />,
          roles: [100],
        },
        {
          path: '/dashboard/reports/live-time',
          element: <LiveTimeReport />,
          roles: [100],
        },
      ],
    },
    {
      path: 'reports-2',
      element: <Outlet />,
      roles: [100],
      children: [
        {
          index: true,
          element: <Reports2 />,
          roles: [100],
        },
      ],
    },
    {
      path: 'reports',
      element: <Outlet />,
      roles: [100, 150, 200, 550],
      children: [
        {
          index: true,
          element: <ReportStore />,
          roles: [100, 150, 200, 550],
        },
      ],
    },
    {
      path: 'campaigns',
      element: <Outlet />,
      roles: [100, 150, 200],
      children: [
        {
          index: true,
          element: <Coupons />,
          roles: [100, 150, 200],
        },
        {
          path: 'archive',
          element: <ArchivedCampaigns />,
          roles: [100, 150, 200],
        },
      ],
    },
    {
      path: 'referral',
      element: <Outlet />,
      roles: [150, 200],
      children: [
        {
          index: true,
          element: <Referral />,
          roles: [150, 200],
        },
      ],
    },
    {
      path: 'image-library',
      element: <Outlet />,
      roles: [100],
      children: [
        {
          index: true,
          element: <ImageLibraryAdmin />,
          roles: [100],
        },
      ],
    },
    {
      // What's new (spec 2026-10-16 §2): every dashboard role reads the
      // published release notes; the admin surface lives under settings.
      path: 'release-notes',
      element: <Outlet />,
      roles: [100, 150, 200, 250, 300, 550],
      children: [
        {
          index: true,
          element: <ReleaseNotes />,
          roles: [100, 150, 200, 250, 300, 550],
        },
        {
          path: '/dashboard/release-notes/:slug',
          element: <ReleaseNote />,
          roles: [100, 150, 200, 250, 300, 550],
        },
      ],
    },
    {
      path: 'settings',
      element: <Outlet />,
      roles: [100, 150, 200, 250, 300, 550],
      children: [
        {
          index: true,
          path: 'profile',
          element: <Profile />,
          roles: [100, 150, 200, 250, 300, 550],
        },
        {
          index: false,
          path: 'bank-accounts',
          element: <Navigate to="/dashboard/finance/bank-account" replace />,
          // Matches the target route's own roles ([150, 200]) — an admin
          // (100) let through here would pass this redirect's own check,
          // then fail hasRoutePermisions on the destination and land on
          // '/dashboard' with no explanation instead of a permission error.
          roles: [150, 200],
        },
        {
          index: false,
          path: 'payments',
          element: <Navigate to="/dashboard/finance/business-details" replace />,
          // Same reasoning as 'bank-accounts' above.
          roles: [150, 200],
        },
        {
          index: false,
          path: 'permissions',
          element: <Permission />,
          roles: [100],
        },
        {
          index: false,
          path: 'users',
          element: <Users />,
          roles: [100, 150, 200],
        },
        {
          index: false,
          path: 'integrations',
          element: <Integrations />,
          roles: [100, 150, 200],
        },
        {
          path: 'square/callback',
          element: <SquareCallback />,
          roles: [100, 150, 200],
        },
        {
          path: 'clover/callback',
          element: <CloverCallback />,
          roles: [100, 150, 200],
        },
        {
          index: false,
          path: 'subscription',
          element: <Subscription />,
          roles: [100, 150, 200],
        },
        {
          index: false,
          path: 'subscriptions',
          element: <Subscriptions />,
          roles: [100],
        },
        {
          index: false,
          path: 'tags',
          element: <Tags />,
          roles: [100],
        },
        {
          index: false,
          path: 'templates',
          element: <Templates />,
          roles: [100],
        },

        {
          index: false,
          path: 'finance-types',
          element: <FinanceTypes />,
          roles: [100],
        },
        {
          index: false,
          path: 'preferences',
          element: <Preferences />,
          roles: [100, 150, 200, 250, 300],
        },
        {
          index: false,
          path: 'blocked-numbers',
          element: <BlockedNumbers />,
          roles: [100, 150, 200],
        },
        {
          index: false,
          path: 'printer-profiles',
          element: <PrinterProfiles />,
          roles: [100, 150, 200],
        },
        {
          index: false,
          path: 'terms-and-conditions',
          element: <TermsAndConditions />,
          roles: [100],
        },
        {
          index: false,
          path: 'term-acceptances',
          element: <TermAcceptances />,
          roles: [100],
        },
        {
          index: false,
          path: 'referral',
          element: <ReferralAdmin />,
          roles: [100],
        },
        {
          index: false,
          path: 'ratings',
          element: <Ratings />,
          roles: [100],
        },
        {
          index: false,
          path: 'login-logs',
          element: <LoginLogs />,
          roles: [100],
        },
        {
          index: false,
          path: 'legal',
          element: <Legal />,
          roles: [150, 200],
        },
        {
          // Self-gated page (spec 2026-10-16 §2): the router knows roles only,
          // the permission holder is admitted by the page; the API 422s otherwise.
          index: false,
          path: 'release-notes',
          element: <ReleaseNotesAdmin />,
          roles: [100, 150, 200, 250, 300, 550],
        },
        {
          // Same self-gated editor for both (spec §7.3); React Router ranks the
          // static `new` segment above `:id`.
          index: false,
          path: 'release-notes/new',
          element: <ReleaseNoteEditor />,
          roles: [100, 150, 200, 250, 300, 550],
        },
        {
          index: false,
          path: 'release-notes/:id',
          element: <ReleaseNoteEditor />,
          roles: [100, 150, 200, 250, 300, 550],
        },
      ],
    },
  ];

  const hasRoutePermisions = (roles) => {
    const filteredRoutes = routes
      .map((item) => {
        if (item.children) {
          const filteredChildren = item.children.filter((child) =>
            roles?.some((role) => child.roles.includes(role)),
          );
          return filteredChildren.length
            ? { ...item, children: filteredChildren, hidden: false }
            : { ...item, hidden: true };
        } else {
          return roles?.some((role) => item.roles.includes(role))
            ? { ...item, hidden: false }
            : { ...item, hidden: true };
        }
      })
      .filter((item) => !item.hidden);
    return filteredRoutes;
  };

  const router = createBrowserRouter([
    {
      // Pathless wrapper: render hataları çirkin varsayılan ekran yerine
      // RouteError'a düşsün (offline tespiti + otomatik yenileme).
      errorElement: <RouteError />,
      children: [
        {
          path: '*',
          element: <Navigate to="/" replace />,
        },
        {
          path: '/',
          element: <Login />,
        },
        {
          path: '/forgot-password',
          element: <ForgotPassword />,
        },
        {
          path: '/new-password',
          element: <NewPassword />,
        },
        {
          path: '/register',
          element: <Register />,
        },
        {
          path: '/contact-us',
          element: <ContactUs />,
        },
        turnstileBridgeRoute,
        {
          path: '/receipt/:id',
          element: <Receipt />,
        },
        {
          path: '/dashboard',
          element: (
            <AuthProvider>
              <MerchantAcceptanceGuard>
                <Layout />
              </MerchantAcceptanceGuard>
            </AuthProvider>
          ),
          children: hasRoutePermisions(roles),
        },
      ],
    },
  ]);

  if (tokenService.getLocalAccessToken()) {
    dispatch(setIsAuthenticated());
  }
  return <RouterProvider router={router} />;
};

export default Router;
